// Winnipeg neighborhood pages — granular long-tail SEO under
// /cities/winnipeg/[neighborhood]. Each neighborhood entry maps to a
// character brief, a coverage focus, and the services most relevant to
// the built environment of that area.

export const neighborhoods = [
  {
    slug: "portage-and-main",
    citySlug: "winnipeg",
    name: "Portage & Main",
    tag: "Winnipeg's financial core.",
    hero: "The intersection that has defined downtown Winnipeg for over a century — the financial district's corporate towers, the flagship bank branches, and the executive floors above them.",
    sections: [
      {
        h: "The address book of downtown Winnipeg",
        p: "Portage & Main is not a neighbourhood in the residential sense — it is the corporate address of downtown Winnipeg. The Richardson Building, 201 Portage, 360 Main, the Trust Building, the CanWest Global Place, the Manitoba Hydro tower, and the buildings that surround the intersection carry the province's financial, professional-services, and corporate-headquarters footprint. Manitoba Security Services staffs concierge, lobby, and executive-floor postings across the corridor with officers selected for presentation, communication precision, and the specific discipline required to represent a corporate address to every visitor who crosses the threshold."
      },
      {
        h: "What Portage & Main coverage looks like",
        p: "A well-run Portage & Main posting manages visitor systems from paper logs to enterprise platforms, verifies contractor credentials, coordinates after-hours access on restricted floors, integrates with existing electronic access control, and escalates anomalies to facilities and HR. Officers train to each client's evacuation plan, fire-warden hierarchy, and executive-visit protocol. Where higher-profile visits are on the calendar — board meetings, sensitive terminations, high-value client tours — we scale to a plainclothes protective posture without disrupting the day-to-day feel of the building."
      },
      {
        h: "Mobile patrol along the Portage corridor",
        p: "Beyond the lobby, mobile patrol coverage along Portage Avenue services after-hours retail, hospitality, and mixed-use properties from Main Street west through the entertainment district. Randomised, GPS-verified passes protect against opportunistic theft, verify closure procedures, and support alarm-response cycles for monitored premises across the corridor."
      }
    ],
    services: ["corporate-security", "concierge-security", "executive-protection", "mobile-patrol"],
    keywords: [
      "Portage and Main security",
      "downtown Winnipeg security",
      "corporate security Portage Main",
      "financial district security Winnipeg",
      "office tower security downtown Winnipeg"
    ]
  },
  {
    slug: "exchange-district",
    citySlug: "winnipeg",
    name: "Exchange District",
    tag: "Heritage buildings. Creative businesses. A hospitality footprint.",
    hero: "The Exchange District — a National Historic Site of Canada, and a working commercial neighbourhood carrying restaurants, boutique hotels, creative agencies, and mixed-use residential blocks inside heritage buildings.",
    sections: [
      {
        h: "A heritage district with modern operational demands",
        p: "The Exchange District's character — brick-and-timber warehouses, wide sidewalks, cast-iron facades, and the alley networks that separate them — creates a specific security environment. Ground-floor hospitality operators need trained event and licensed-hospitality coverage; upper-floor creative businesses need after-hours patrol for expensive equipment; the boutique hotel footprint needs concierge-grade posture. Manitoba Security Services deploys officers matched to the tone of the district — professional, unobtrusive, and calibrated to a neighbourhood that treats presentation as part of the product."
      },
      {
        h: "Event, mobile patrol, and hospitality coverage",
        p: "The Exchange District hosts a substantial event calendar — Winnipeg Fringe Festival footprint, Manitoba Electronic Music Exhibition, the Cube at Old Market Square, and dozens of private events at heritage venues each year. Our event officers manage crowd flow, licensed-alcohol verification under Manitoba's LGCA requirements, and coordinated egress. Between events, mobile patrol covers commercial closures, monitored alarm response, and after-hours perimeter passes across the district's alley network."
      },
      {
        h: "Retail and boutique-hotel security",
        p: "Ground-floor retail across the district benefits from mixed uniformed and plainclothes coverage — visible deterrence at doors, structured observation of high-risk product zones, and clean apprehension protocol under the six-step model. Boutique hotel front desks are staffed with hospitality-forward concierge officers who deliver security posture without compromising guest experience."
      }
    ],
    services: ["event-security", "mobile-patrol", "concierge-security", "retail-loss-prevention"],
    keywords: [
      "Exchange District security",
      "heritage district security Winnipeg",
      "Winnipeg Fringe security",
      "Old Market Square security",
      "boutique hotel security Winnipeg"
    ]
  },
  {
    slug: "osborne-village",
    citySlug: "winnipeg",
    name: "Osborne Village",
    tag: "Dense residential. Ground-floor retail. Nightlife.",
    hero: "Osborne Village — one of the highest-density residential neighbourhoods in Western Canada, layered with independent retail, restaurants, and a compact nightlife footprint.",
    sections: [
      {
        h: "Density, foot traffic, and mixed-use security demand",
        p: "Osborne Village runs on a specific rhythm: high resident density in mid-rise and high-rise buildings, ground-floor independent retail along Osborne Street, and an evening hospitality footprint that shifts the population profile after dark. Manitoba Security Services covers this mix with condominium concierge officers, mobile patrol across the residential streets, retail loss-prevention support for independent operators, and event coverage for the summer festivals that fill Osborne Village's commercial corridor."
      },
      {
        h: "Concierge and residential coverage",
        p: "Higher-rise residential in the Village — the buildings that line River Avenue and Roslyn Road — is staffed with concierge officers selected for warmth, discretion, and the specific composure required to hold a front desk through a full shift. Access is managed under a documented protocol, life-safety panels are monitored, and community bylaws are enforced with tact."
      },
      {
        h: "Nightlife-adjacent event and mobile coverage",
        p: "For hospitality operators along Osborne, we deploy licensed-hospitality-trained officers for event nights and provide mobile patrol coverage that supports overnight closure procedures. Randomised GPS-verified passes hit every commercial block on schedules that never repeat, backed by the audit trail that Osborne insurance underwriters require."
      }
    ],
    services: ["residential-security", "concierge-security", "mobile-patrol", "retail-loss-prevention", "event-security"],
    keywords: [
      "Osborne Village security",
      "condo concierge Osborne Winnipeg",
      "residential security Osborne Village",
      "Roslyn Road security",
      "Osborne Street patrol"
    ]
  },
  {
    slug: "st-boniface",
    citySlug: "winnipeg",
    name: "St. Boniface",
    tag: "Winnipeg's francophone quarter. Institutional and hospitality footprint.",
    hero: "St. Boniface — the francophone heart of Manitoba, home to the Saint-Boniface Hospital, Université de Saint-Boniface, cultural institutions, and one of the province's most active hospitality corridors along Provencher Boulevard.",
    sections: [
      {
        h: "An institutional and community coverage area",
        p: "St. Boniface carries a security profile shaped by its institutions. The Saint-Boniface Hospital and its surrounding medical campus demand healthcare-trained officers with de-escalation and code-response experience. Université de Saint-Boniface requires campus coverage that respects the tone of a bilingual post-secondary environment. The cathedral, the Musée de Saint-Boniface, the Centre culturel franco-manitobain, and the Provencher hospitality strip round out an environment that benefits from officers who can operate in both official languages."
      },
      {
        h: "Healthcare, education, and event coverage",
        p: "Our St. Boniface healthcare officers train to Winnipeg Regional Health Authority protocols, respond to code events, and hold the strictest privacy discipline of any posting. Campus coverage supports the university's operational envelope. And for events at the CCFM and along Provencher — Festival du Voyageur adjacent footprints, cultural evenings, private receptions — we deploy event-trained officers with the discretion the district expects."
      },
      {
        h: "Mobile patrol and residential coverage",
        p: "The residential blocks around Rue Aulneau, Rue Deschambault, and Rue Aubry benefit from randomised mobile patrol, alarm response, and — for higher-density buildings — concierge coverage. Officers are selected for the neighbourhood's tone: professional, culturally aware, calm."
      }
    ],
    services: ["healthcare-security", "education-campus-security", "event-security", "mobile-patrol", "residential-security"],
    keywords: [
      "St. Boniface security",
      "Saint-Boniface security",
      "Provencher security Winnipeg",
      "Saint-Boniface Hospital security",
      "bilingual security officer Winnipeg"
    ]
  },
  {
    slug: "bridgwater",
    citySlug: "winnipeg",
    name: "Bridgwater",
    tag: "Master-planned residential. Growing commercial footprint.",
    hero: "Bridgwater — Winnipeg's fastest-growing master-planned residential area, layered with a rapidly expanding commercial and retail footprint at Bridgwater Trails, Forest, Lakes, and Centre.",
    sections: [
      {
        h: "A new-build residential and commercial coverage area",
        p: "Bridgwater's growth has outpaced most of Winnipeg's traditional residential neighbourhoods. Master-planned streetscapes, mid-density condominium developments, single-home enclaves, and a rapidly building commercial retail footprint at Bridgwater Centre have combined to produce a coverage area where security demand ranges from concierge posture in higher-density buildings through mobile patrol on residential streets to retail coverage at the commercial nodes."
      },
      {
        h: "Residential concierge and mobile patrol",
        p: "Higher-density Bridgwater developments benefit from concierge officers selected for warmth and unshakeable composure — the specific mix that carries a front desk through both a routine day and a difficult night. Single-home enclaves and cul-de-sac communities are covered by randomised mobile patrol, alarm response, and scheduled interior/exterior verification service for owners on extended travel."
      },
      {
        h: "Commercial and construction coverage",
        p: "Bridgwater's ongoing construction phase produces steady demand for job-site protection — perimeter fencing coverage, overnight mobile patrols during structural work, and full manned coverage during finishing phases when high-value trades are stacked. Retail coverage at Bridgwater Centre provides visible deterrence and structured loss-prevention support."
      }
    ],
    services: ["residential-security", "concierge-security", "mobile-patrol", "construction-site-security", "retail-loss-prevention"],
    keywords: [
      "Bridgwater security",
      "Bridgwater Trails security",
      "Bridgwater condo security",
      "master-planned community security Winnipeg",
      "Bridgwater Centre security"
    ]
  },
  {
    slug: "sage-creek",
    citySlug: "winnipeg",
    name: "Sage Creek",
    tag: "Master-planned suburb. Family-density residential.",
    hero: "Sage Creek — a master-planned southeast Winnipeg community with a strong single-home and townhouse footprint, layered with a growing commercial and school-adjacent security demand.",
    sections: [
      {
        h: "A residential-first coverage area",
        p: "Sage Creek's built environment is dominated by single-family homes, townhouse blocks, and mid-density residential clusters organized around planned streetscapes. Manitoba Security Services covers Sage Creek with randomised mobile patrol, alarm response, and — for residents on extended travel — a scheduled interior/exterior verification service that documents every visit with photograph confirmation."
      },
      {
        h: "School and community coverage",
        p: "The neighbourhood's schools and community centre generate a distinct security posture during event-driven periods — graduations, community-programming evenings, and shared-use bookings. Our education-trained officers cover these environments with the calm, approachable posture that a school-adjacent setting requires."
      },
      {
        h: "Construction and commercial coverage",
        p: "Sage Creek's continued build-out produces ongoing job-site security demand. Our construction officers cover perimeter fencing during excavation, overnight patrols during structural work, and full manned coverage during finishing phases."
      }
    ],
    services: ["residential-security", "mobile-patrol", "education-campus-security", "construction-site-security"],
    keywords: [
      "Sage Creek security",
      "southeast Winnipeg security",
      "residential patrol Sage Creek",
      "Sage Creek neighbourhood watch",
      "master-planned community security"
    ]
  },
  {
    slug: "tuxedo",
    citySlug: "winnipeg",
    name: "Tuxedo",
    tag: "Estate residential. Discretion required.",
    hero: "Tuxedo — one of Winnipeg's most established estate neighbourhoods, with high-value single-family homes, private-school campuses, and the Assiniboine Park approach.",
    sections: [
      {
        h: "Estate-density coverage",
        p: "Tuxedo's built environment is dominated by high-value estate homes on generous lots — an environment where security posture must be discreet, professional, and consistent. Manitoba Security Services covers Tuxedo with randomised mobile patrol, alarm response, and a scheduled property verification service for homeowners on extended travel."
      },
      {
        h: "Private school and cultural institution coverage",
        p: "Tuxedo hosts several private-school campuses and cultural institutions. Our education-trained officers cover these environments with a posture that respects the tone of the institution — approachable, calm, unmistakably present."
      },
      {
        h: "Executive protection and event coverage",
        p: "For residents who require executive protection — corporate movement, sensitive family events, high-profile receptions — we deploy plainclothes senior officers whose presentation and composure align with the neighbourhood's expectations."
      }
    ],
    services: ["residential-security", "mobile-patrol", "executive-protection", "education-campus-security"],
    keywords: [
      "Tuxedo security Winnipeg",
      "estate security Winnipeg",
      "high-value residential security",
      "executive protection Winnipeg",
      "Assiniboine Park security"
    ]
  },
  {
    slug: "river-heights",
    citySlug: "winnipeg",
    name: "River Heights",
    tag: "Established residential. Commercial retail spine.",
    hero: "River Heights — an established south-Winnipeg residential neighbourhood with a strong commercial retail spine along Corydon Avenue and Grant Avenue.",
    sections: [
      {
        h: "Mixed-use residential and commercial coverage",
        p: "River Heights combines established single-family residential blocks with active commercial corridors — Corydon Avenue's hospitality strip, Grant Avenue's retail spine, and the Grant Park Shopping Centre footprint. Manitoba Security Services covers each with the appropriate posture: mobile patrol on residential streets, retail loss-prevention support at commercial anchors, and event coverage for the summer festival footprint along Corydon."
      },
      {
        h: "Corydon hospitality and event coverage",
        p: "Corydon's restaurant and hospitality corridor benefits from licensed-hospitality-trained officers on event nights, plus mobile patrol coverage that supports overnight closures across the strip."
      },
      {
        h: "Grant Park and retail coverage",
        p: "Grant Park Shopping Centre and the surrounding retail footprint benefit from mixed uniformed and plainclothes loss-prevention coverage — visible deterrence at doors, structured observation, and clean apprehension protocol under Manitoba private-security regulations."
      }
    ],
    services: ["residential-security", "mobile-patrol", "retail-loss-prevention", "event-security", "concierge-security"],
    keywords: [
      "River Heights security",
      "Corydon Avenue security",
      "Grant Park security",
      "Grant Park Shopping Centre security",
      "hospitality security Winnipeg"
    ]
  },
  {
    slug: "the-forks",
    citySlug: "winnipeg",
    name: "The Forks",
    tag: "Winnipeg's meeting place. Cultural, event, and tourism footprint.",
    hero: "The Forks — Winnipeg's most-visited public space, hosting year-round programming, the Canadian Museum for Human Rights, the Forks Market, and one of the province's densest event calendars.",
    sections: [
      {
        h: "Event, tourism, and cultural coverage",
        p: "The Forks operates as a permanent event venue. From the summer festival footprint through winter programming on the Red River Mutual Trail, the site generates a continuous demand for event-trained officers who understand crowd management, cultural-institution etiquette, and coordinated emergency egress. Manitoba Security Services deploys event supervisors and officers for major programming days and provides ongoing support for the Forks Market, the cultural institutions, and the hospitality operators across the site."
      },
      {
        h: "Museum and cultural-institution coverage",
        p: "Cultural institutions adjacent to the site — including the Canadian Museum for Human Rights — benefit from officers trained in the specific posture cultural venues require: approachable, unobtrusive, and calibrated to a visitor experience that treats security as part of the hospitality equation."
      },
      {
        h: "Trail, foot-patrol, and seasonal coverage",
        p: "The Red River Mutual Trail in winter and the walking-path network year-round benefit from scheduled foot patrol during peak visitation windows. Our officers cover these environments with the visible-but-approachable posture the site's programming team expects."
      }
    ],
    services: ["event-security", "concierge-security", "mobile-patrol"],
    keywords: [
      "The Forks security",
      "Canadian Museum for Human Rights security",
      "Winnipeg tourism security",
      "Forks Market security",
      "Red River Trail security"
    ]
  },
  {
    slug: "transcona",
    citySlug: "winnipeg",
    name: "Transcona",
    tag: "Working-class residential. Rail heritage. Industrial adjacency.",
    hero: "Transcona — an east-Winnipeg community with a distinct identity, a strong residential footprint, and industrial adjacency to the Symington rail yard and the East Perimeter distribution corridor.",
    sections: [
      {
        h: "Residential and commercial coverage",
        p: "Transcona's residential footprint runs from established mid-century blocks through newer subdivisions in the Devonshire Park and Kildare areas. Manitoba Security Services covers Transcona with mobile patrol, alarm response, and scheduled residential verification for owners on extended travel. Commercial coverage supports the Transcona commercial corridor along Regent Avenue and the neighbourhood's independent retail base."
      },
      {
        h: "Industrial adjacency and warehouse coverage",
        p: "The Symington rail yard and the industrial and warehouse footprints along the East Perimeter Highway generate distinct coverage demand — gate control, dockyard security, cargo integrity, and driver check-in for the logistics operators serving greater Winnipeg's east flank."
      }
    ],
    services: ["residential-security", "mobile-patrol", "warehouse-industrial-security", "retail-loss-prevention"],
    keywords: [
      "Transcona security",
      "East Winnipeg security",
      "Symington yard security",
      "Regent Avenue security",
      "East Perimeter warehouse security"
    ]
  },
  {
    slug: "st-james",
    citySlug: "winnipeg",
    name: "St. James",
    tag: "Airport-adjacent. Aerospace corridor. Commercial belt.",
    hero: "St. James — Winnipeg's western commercial and aerospace corridor, anchored by the Winnipeg Richardson International Airport and the surrounding industrial, aerospace, and commercial footprint.",
    sections: [
      {
        h: "Aerospace corridor and industrial coverage",
        p: "St. James carries a specific industrial footprint — Manitoba's aerospace cluster, controlled-goods handling adjacent to the airport, cargo-forwarding operators, and the commercial retail belt along Portage Avenue and Ellice Avenue. Manitoba Security Services staffs the corridor with officers cross-trained for controlled-goods environments, plus gate-control and driver check-in for cargo operators."
      },
      {
        h: "Commercial retail and hospitality coverage",
        p: "The Polo Park Shopping Centre footprint sits at the eastern edge of St. James and generates substantial retail loss-prevention demand. Our officers cover the mall's tenant zones with visible deterrence and structured plainclothes observation, backed by the six-step apprehension model where the client's policy authorises. Hotel corridors along Portage Avenue benefit from concierge-grade coverage."
      },
      {
        h: "Airport-adjacent event and mobile coverage",
        p: "Business travel volume through Winnipeg Richardson International Airport produces steady demand for executive-protection interface, hotel-to-venue movement coverage, and mobile patrol across the airport's business-park footprint."
      }
    ],
    services: ["warehouse-industrial-security", "retail-loss-prevention", "concierge-security", "executive-protection", "mobile-patrol"],
    keywords: [
      "St. James security",
      "Polo Park security",
      "Winnipeg airport security services",
      "aerospace corridor security Winnipeg",
      "Portage Avenue west security"
    ]
  },
  {
    slug: "fort-garry",
    citySlug: "winnipeg",
    name: "Fort Garry",
    tag: "University-adjacent. Residential density. Industrial edge.",
    hero: "Fort Garry — home to the University of Manitoba's Fort Garry campus, a substantial residential footprint, and the Fort Garry industrial belt along the Red River's south edge.",
    sections: [
      {
        h: "University campus and student-housing coverage",
        p: "The University of Manitoba's Fort Garry campus operates as one of Manitoba's largest employment and gathering footprints. Manitoba Security Services provides campus-trained officers where clients require, plus adjacent commercial and retail coverage across the Pembina Highway spine that serves the student population. Officers are selected for the specific tone of a post-secondary environment — approachable, calm, unmistakably present."
      },
      {
        h: "Residential and mobile patrol coverage",
        p: "The residential blocks around the university — from Wildwood through Waverley Heights and Whyte Ridge — benefit from randomised mobile patrol, alarm response, and property verification service. Coverage extends south through Waverley West as the neighbourhood continues to grow."
      },
      {
        h: "Industrial coverage along the Red River south edge",
        p: "The Fort Garry industrial belt generates gate-control, dockyard, and cargo-integrity demand across a stable tenant base. Our officers support after-hours patrol, driver check-in, and seal verification against the client's WMS gate module."
      }
    ],
    services: ["education-campus-security", "residential-security", "mobile-patrol", "warehouse-industrial-security"],
    keywords: [
      "Fort Garry security",
      "U of M campus security",
      "Pembina Highway security",
      "Waverley West security",
      "Fort Garry industrial security"
    ]
  },
  {
    slug: "waverley-west",
    citySlug: "winnipeg",
    name: "Waverley West",
    tag: "Fast-growth residential. Commercial expansion.",
    hero: "Waverley West — one of Winnipeg's fastest-growing planned areas, with active residential construction, expanding commercial retail, and a family-density residential profile.",
    sections: [
      {
        h: "Growth-area residential coverage",
        p: "Waverley West's continued build-out — through South Pointe, Prairie Pointe, and the surrounding neighbourhoods — produces steady demand for residential mobile patrol, alarm response, and property verification. Higher-density condominium buildings across the area benefit from concierge posture; single-home enclaves and townhouse blocks benefit from randomised patrol."
      },
      {
        h: "Construction site coverage",
        p: "Active construction across Waverley West generates ongoing job-site security demand. Our construction officers cover perimeter fencing, laydown yards, and full manned coverage during finishing phases when high-value trades are stacked and copper-theft exposure is highest."
      },
      {
        h: "Commercial and retail coverage",
        p: "The commercial nodes serving Waverley West's residential base — big-box footprints along Kenaston Boulevard, service-retail across the neighbourhood centres — benefit from mixed uniformed and plainclothes loss-prevention coverage."
      }
    ],
    services: ["residential-security", "mobile-patrol", "construction-site-security", "concierge-security", "retail-loss-prevention"],
    keywords: [
      "Waverley West security",
      "South Pointe security",
      "Kenaston security",
      "Prairie Pointe security",
      "new-build community security Winnipeg"
    ]
  },
  {
    slug: "charleswood",
    citySlug: "winnipeg",
    name: "Charleswood",
    tag: "Established residential. Wooded lots. Rural adjacency.",
    hero: "Charleswood — an established southwestern Winnipeg neighbourhood with distinctive wooded lots, larger residential parcels, and a rural-community feel inside city limits.",
    sections: [
      {
        h: "Established estate-density coverage",
        p: "Charleswood's built environment favours larger residential parcels, mature tree cover, and generous setbacks. Manitoba Security Services covers Charleswood with randomised mobile patrol, alarm response, and a scheduled property verification service for owners on extended travel. Officers are selected for the neighbourhood's tone: quiet, professional, discreet."
      },
      {
        h: "Community and event coverage",
        p: "Charleswood's community centre and the Assiniboine Forest approach generate event-driven coverage demand throughout the year. Our event-trained officers support programming with the calm, approachable posture the community expects."
      }
    ],
    services: ["residential-security", "mobile-patrol", "event-security"],
    keywords: [
      "Charleswood security",
      "southwest Winnipeg security",
      "Assiniboine Forest security",
      "estate residential security Winnipeg"
    ]
  },
  {
    slug: "st-vital",
    citySlug: "winnipeg",
    name: "St. Vital",
    tag: "Family residential. Retail centre. Community anchor.",
    hero: "St. Vital — a stable southeast Winnipeg residential community anchored by the St. Vital Centre mall footprint and the surrounding retail spine.",
    sections: [
      {
        h: "Retail and mall-anchor coverage",
        p: "The St. Vital Centre footprint and the surrounding retail belt along St. Anne's Road and Bishop Grandin Boulevard generate steady loss-prevention demand. Manitoba Security Services covers this footprint with mixed uniformed and plainclothes officers — visible deterrence at entrances, structured observation of high-risk product zones, and clean apprehension protocol backed by the six-step observation model."
      },
      {
        h: "Residential and mobile patrol coverage",
        p: "St. Vital's residential blocks — Norwood, Norberry, Meadowood, Windsor Park adjacencies — benefit from randomised mobile patrol, alarm response, and property verification service. Officers hit critical zones on schedules that never repeat and document every pass with photograph evidence."
      }
    ],
    services: ["retail-loss-prevention", "residential-security", "mobile-patrol", "concierge-security"],
    keywords: [
      "St. Vital security",
      "St. Vital Centre security",
      "Bishop Grandin security",
      "Meadowood security",
      "southeast Winnipeg patrol"
    ]
  }
];

export const neighborhoodBySlug = (slug) =>
  neighborhoods.find((n) => n.slug === slug);

export const neighborhoodsByCity = (citySlug) =>
  neighborhoods.filter((n) => n.citySlug === citySlug);
